import {
  getOrdersByParams,
  type OrdersByParamsOptionsType
} from '../../../api/ordersApi';
import { CANCEL_CODE, type IErrorData } from '../../../utils/getErrorData';
import { isError } from '../../../utils/typeguards/isError';
import { isAxiosAbortError } from '../../../utils/typeguards/isAxiosAbortError';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { type IOrderListItem } from '@/types/IOrder';

const ABORT_MESSAGE = 'Запрос отменен';

/**
 * Определяет, была ли ошибка вызвана отменой запроса (abort), чтобы
 * отличить её от настоящей ошибки загрузки.
 * @param error Перехваченная ошибка
 * @returns `true`, если запрос был отменён
 */
const isRequestAborted = (error: unknown): boolean =>
  isAxiosAbortError(error) ||
  (error instanceof Error && error.message === ABORT_MESSAGE);

export const getOrdersThunk = createAsyncThunk<
  { items: IOrderListItem[]; total: number },
  OrdersByParamsOptionsType,
  { rejectValue: IErrorData }
>('orders/getOrdersThunk', async (options, { rejectWithValue }) => {
  try {
    const data = await getOrdersByParams(options);

    if (isError(data)) {
      return rejectWithValue(data);
    }

    if (
      typeof data !== 'object' ||
      data === null ||
      !('response' in data) ||
      !Array.isArray(data.response.items)
    ) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return {
      items: data.response.items,
      total: data.response.meta?.total ?? 0
    };
  } catch (error) {
    if (isRequestAborted(error)) {
      return rejectWithValue({ message: ABORT_MESSAGE, code: CANCEL_CODE });
    }

    return rejectWithValue({ message: 'Ошибка при получении заказов' });
  }
});
